import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, Input, Button, Divider, Alert, List, Typography } from 'antd';
import { Sidebar } from '@/components/sidebar';
import UploadMultipleFile from '@/components/upload-multiple-file/UploadMultipleFile';
import { useNotify } from '@/hooks';
import { API } from '@/services/api.service';

const { Text } = Typography;
const { TextArea } = Input;

/**
 * Send Custom Email Drawer Component
 *
 * Drawer for sending custom emails to users with:
 * - Subject input
 * - Body text area
 * - File attachments (max 5 files, 10MB each)
 *
 * @param {boolean} open - Drawer visibility
 * @param {Function} onClose - Close handler
 * @param {Array<string>} selectedRowKeys - Array of user IDs to send email to
 * @param {string} queryKey - Query key for cache invalidation
 */
export default function SendCustomEmailDrawer({
  open,
  onClose,
  selectedRowKeys,
  queryKey,
}) {
  const { t } = useTranslation(['forms', 'messages']);
  const notification = useNotify();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [attachments, setAttachments] = useState([]);
  const [result, setResult] = useState(null);

  const handleClose = () => {
    form.resetFields();
    setAttachments([]);
    setResult(null);
    onClose && onClose();
  };

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (values) => {
      return await API.users.sendCustomEmail({
        userIds: selectedRowKeys,
        subject: values.subject,
        body: values.body,
        attachments,
      });
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries([queryKey]);

      if (response?.successCount > 0) {
        setResult(response);
        notification.success({
          message: t('success', { ns: 'messages' }),
          description: t('customEmailSentSuccess', {
            count: response.successCount,
            ns: 'forms',
          }),
        });
      } else {
        setResult(response);
      }
    },
    onError: (error) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: error.response?.data?.message || error.message,
      });
    },
  });

  const onFinish = async (values) => {
    await mutateAsync(values);
  };

  const handleFilesChange = (files) => {
    setAttachments(files);
  };

  const renderResultContent = () => {
    if (!result) return null;

    return (
      <div className="space-y-4">
        {result.successCount > 0 && (
          <Alert
            type="success"
            message={t('customEmailSentSuccessCount', {
              count: result.successCount,
              ns: 'forms',
            })}
            showIcon
          />
        )}

        {result.skippedCount > 0 && (
          <Alert
            type="warning"
            message={t('emailsSkippedCount', {
              count: result.skippedCount,
              ns: 'forms',
            })}
            description={
              <List
                size="small"
                dataSource={result.skipped}
                renderItem={(item) => (
                  <List.Item>
                    <Text>{item.name}</Text>
                    <Text type="secondary" className="ml-2">
                      - {item.reason || t('skipped', { ns: 'forms' })}
                    </Text>
                  </List.Item>
                )}
              />
            }
            showIcon
          />
        )}

        {result.failedCount > 0 && (
          <Alert
            type="error"
            message={t('emailsFailedCount', {
              count: result.failedCount,
              ns: 'forms',
            })}
            description={
              <List
                size="small"
                dataSource={result.failed}
                renderItem={(item) => (
                  <List.Item>
                    <Text>{item.name}</Text>
                    <Text type="secondary" className="ml-2">
                      - {item.reason}
                    </Text>
                  </List.Item>
                )}
              />
            }
            showIcon
          />
        )}

        <Divider style={{ backgroundColor: '#2db1ab50', margin: '20px 0' }} />

        <Button
          block
          type="primary"
          onClick={handleClose}
          className="btn-primary"
        >
          {t('close', { ns: 'forms' })}
        </Button>
      </div>
    );
  };

  return (
    <Sidebar
      title={t('sendCustomEmail', { ns: 'forms' })}
      placement="right"
      onClose={handleClose}
      open={open}
    >
      <div className="p-6">
        {result ? (
          renderResultContent()
        ) : (
          <>
            <p className="mb-4 text-sm text-gray-600">
              {t('sendCustomEmailDescription', {
                count: selectedRowKeys?.length || 0,
                ns: 'forms',
              })}
            </p>

            <Divider style={{ backgroundColor: '#2db1ab50', margin: '20px 0' }} />

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              className="form"
            >
              <Form.Item
                name="subject"
                label={t('emailSubject', { ns: 'forms' })}
                rules={[
                  {
                    required: true,
                    message: t('requiredField', { ns: 'forms' }),
                  },
                ]}
              >
                <Input placeholder={t('enterEmailSubject', { ns: 'forms' })} />
              </Form.Item>

              <Form.Item
                name="body"
                label={t('emailBody', { ns: 'forms' })}
                rules={[
                  {
                    required: true,
                    message: t('requiredField', { ns: 'forms' }),
                  },
                ]}
              >
                <TextArea
                  rows={6}
                  placeholder={t('enterEmailBody', { ns: 'forms' })}
                />
              </Form.Item>

              <Form.Item label={t('attachments', { ns: 'forms' })}>
                <UploadMultipleFile
                  onFilesChange={handleFilesChange}
                  maxFiles={5}
                  maxFileSize={10}
                />
              </Form.Item>

              <Divider style={{ backgroundColor: '#2db1ab50', margin: '20px 0' }} />

              <div className="form-footer">
                <Button
                  block
                  type="primary"
                  loading={isPending}
                  htmlType={!isPending ? 'submit' : 'button'}
                  className="btn-primary"
                >
                  {isPending
                    ? t('sendingEmail', { ns: 'forms' })
                    : t('sendEmail', { ns: 'forms' })}
                </Button>
              </div>
            </Form>
          </>
        )}
      </div>
    </Sidebar>
  );
}
